import { ErrorCategory } from './ErrorCategories.js';
import { ErrorCode } from './ErrorCodes.js';
import { sanitizeContext } from './sanitizeContext.js';

// The one base every SDK error family extends (Milestone 6.10/ADR-026).
//
// It answers the four questions the milestone posed - what went wrong
// (`code`/`message`), where (`category`/`subsystem`/`operation`), can the
// publisher recover (`recoverable`), and what context is available
// (`context`/`cause`) - and it does nothing else. In particular it performs no
// retry, fallback, or skip behavior: an error DESCRIBES a failure, and the
// owning subsystem decides what to do about it (ADR-026, "error policy stays
// outside error classes").
//
// Four properties are deliberately distinct, and conflating them is the
// mistake this class exists to prevent:
//
//   name      JavaScript/class identity. Preserved exactly as it was for
//             every pre-6.10 class ('WrapperLoopError', 'AudioStateError', …).
//             Writable, because every existing subclass assigns it after
//             super() and must keep working.
//   code      The stable machine-readable SDK contract. NEW. This is what a
//             publisher branches on.
//   category  The broad family, for coarse handling.
//   message   Human-readable. Existing messages are preserved verbatim.
//
// Immutability: `code`, `category`, `recoverable`, `context` and `timestamp`
// are defined non-writable and non-configurable so a downstream consumer
// cannot rewrite an error's meaning. The instance itself is NOT frozen - that
// would break both standard Error behavior (`stack` assignment) and every
// existing subclass, all of which set their own fields (`url`, `depth`,
// `status`, …) after calling super().
export class SDKError extends Error {
  constructor(
    message,
    {
      code = ErrorCode.SDK_INTERNAL_ERROR,
      category = ErrorCategory.INTERNAL,
      recoverable = false,
      cause = null,
      context = {},
      subsystem = null,
      operation = null,
    } = {},
  ) {
    super(message);

    this.name = 'SDKError';

    // `cause` is assigned only when one exists, matching what every pre-6.10
    // error family already did - `'cause' in error` stays false otherwise.
    if (cause) {
      this.cause = cause;
    }

    defineReadOnly(this, 'code', code);
    defineReadOnly(this, 'category', category);
    defineReadOnly(this, 'recoverable', !!recoverable);
    defineReadOnly(this, 'subsystem', subsystem);
    defineReadOnly(this, 'operation', operation);
    defineReadOnly(this, 'timestamp', Date.now());
    defineReadOnly(this, 'context', sanitizeContext(context));
  }

  // The shape that crosses a public boundary - Publisher Events, Diagnostics,
  // an exported support bundle. Deliberately omits `stack` and `cause`: a
  // stack is an implementation detail, and a cause chain can hold arbitrary
  // third-party objects. Both remain available on the instance itself for
  // debugging in the console.
  toSafeObject() {
    return {
      name: this.name,
      code: this.code,
      category: this.category,
      message: this.message,
      recoverable: this.recoverable,
      subsystem: this.subsystem,
      operation: this.operation,
      timestamp: this.timestamp,
      context: this.context,
    };
  }

  toJSON() {
    return this.toSafeObject();
  }

  // Walks `cause` to the root. Bounded so a cyclic chain (which a
  // third-party library can produce) cannot hang the caller.
  rootCause() {
    let current = this;
    const seen = new Set();

    while (current?.cause && !seen.has(current.cause) && seen.size < 16) {
      seen.add(current.cause);
      current = current.cause;
    }

    return current === this ? null : current;
  }
}

function defineReadOnly(target, key, value) {
  Object.defineProperty(target, key, {
    value,
    writable: false,
    enumerable: true,
    configurable: false,
  });
}

// The generic wrapper for a failure that is not a meaningful, actionable SDK
// condition - an unexpected throw from a code path that should not have
// thrown. Per ADR-026 constraint 5, most raw `new Error()` sites are NOT given
// bespoke classes; they normalize to this at a boundary instead, so an
// implementation detail never becomes public API by accident.
export class InternalError extends SDKError {
  // `recoverable` defaults to false - an unrecognized failure is assumed
  // fatal - but a boundary may override it when it legitimately knows better.
  // An unclassified throw during an audio ad break is recoverable, because
  // the AdFailurePolicy is about to fall back to content regardless of what
  // the underlying failure was.
  constructor(
    message,
    { cause = null, context = {}, subsystem = null, operation = null, recoverable = false } = {},
  ) {
    super(message, {
      code: ErrorCode.SDK_INTERNAL_ERROR,
      category: ErrorCategory.INTERNAL,
      recoverable,
      cause,
      context,
      subsystem,
      operation,
    });
    this.name = 'InternalError';
  }
}
